<template>
  <thead>
    <tr>
      <th v-if="expandable" class="checkbox-expanded table-hover-bg">
        <BootstrapCheck
          id="checkbox-expand-all"
          v-model="modelExpanded"
          button
          :disabled="disabled"
          :icon="
            modelExpanded
              ? 'material-symbols:keyboard-arrow-up'
              : 'material-symbols:keyboard-arrow-down'
          "
        />
      </th>
      <th v-if="selectable" class="checkbox-selected table-hover-bg">
        <BootstrapCheck id="checkbox-select-all" v-model="modelSelected" :disabled="disabled" />
      </th>
      <th
        v-for="(column, index) in cols"
        :key="index"
        :class="['table-hover-bg', {header: props.sortable && !disabled}]"
        :aria-sort="ariaSort(column)"
        @click="disabled === false && handleSort(column)"
      >
        <span class="d-flex align-items-center justify-content-between text-nowrap">
          <span class="d-inline-flex align-items-center gap-1">
            {{ translate(column) }}
            <BootstrapTooltip v-if="tooltips?.[column]" :title="translate(tooltips[column])">
              <Icon icon="material-symbols:info-outline" />
            </BootstrapTooltip>
          </span>
          <Icon
            v-if="sortable && !disabled && sortKey !== column"
            icon="material-symbols:filter-alt"
            class="filter-icon flex-shrink-0"
          />
          <Icon
            v-if="sortable && !disabled && sortKey === column"
            :icon="
              sortDirection === 1
                ? 'material-symbols:arrow-downward'
                : 'material-symbols:arrow-upward'
            "
            class="flex-shrink-0"
          />
        </span>
      </th>
      <th v-if="updatableInline || deletableInline" class="table-hover-bg"></th>
    </tr>
  </thead>
</template>
<script lang="ts" setup>
import {useLocalisation} from '@/composables/useLocalisation';

const {translate} = useLocalisation();

type Props = {
  columns: string[];
  defaultColumns: string[];
  expandable: boolean;
  expanded: boolean;
  selectable: boolean;
  selected: boolean;
  sortable?: boolean;
  sortKey: string | undefined;
  sortDirection: number;
  disabled: boolean;
  /** Info tooltip per column, keyed by the column label */
  tooltips?: {[column: string]: string};
  updatableInline?: boolean;
  deletableInline?: boolean;
};
const props = withDefaults(defineProps<Props>(), {sortable: false, tooltips: undefined});

const emits = defineEmits<{
  'update:selected': [selected: boolean];
  'update:expanded': [expanded: boolean];
  'update:sortDirection': [column: string];
}>();

const modelSelected = computed({
  get: () => props.selected,
  set: (value) => emits('update:selected', value),
});
const modelExpanded = computed({
  get: () => props.expanded,
  set: (value) => emits('update:expanded', value),
});

const handleSort = (column: string) => {
  if (props.sortable) emits('update:sortDirection', column);
};

const ariaSort = (column: string) => {
  if (props.sortable && props.disabled === false) {
    return props.sortKey === column ? 'ascending' : 'descending';
  } else {
    return 'none';
  }
};

const cols = computed(() => (props.columns.length > 0 ? props.columns : props.defaultColumns));
</script>
<style lang="scss" scoped>
.header {
  cursor: pointer;

  .filter-icon {
    visibility: hidden;
  }
}

.header:hover {
  .filter-icon {
    visibility: visible;
  }
}

.checkbox-expanded :deep(.btn) {
  border: 0;
}

.table-hover-bg {
  // Same value used for table row hover
  background-color: rgb(var(--bs-emphasis-color-rgb) / 7.5%);
}
</style>
